import { useMutation, useQuery } from '@tanstack/react-query'
import { formatarCnpj, percentualPpm, ROTULO_REGIME_CONTRAPARTE, type RegimeContraparte } from '@via-tax/contracts'
import { Aviso, Botao, Selo } from '@via-tax/ui'
import { mesCurto, reais } from '../formatos'
import { RepasseContraparte } from './RepasseContraparte'
import type { CasoConcretoContraparte } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * O contrato com uma contraparte, mês a mês, com as notas reais do período — sem anualizar e sem média.
 * É a tela para decidir caso a caso: "este cliente, estas notas, este crédito".
 */
export function CasoConcreto({ contraparteId, papel, fechar }: { contraparteId: string; papel: 'FORNECEDOR' | 'CLIENTE'; fechar: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.contraparte.casoConcreto.queryOptions({ contraparteId }))
  // O estudo em PDF sai pela fila, como os outros documentos: o botão pede e a tela Relatórios entrega.
  const gerar = useMutation(trpc.relatorio.gerar.mutationOptions())
  return (
    <PainelCasoConcreto
      d={q.data}
      carregando={q.isPending}
      erro={q.error}
      papel={papel}
      fechar={fechar}
      estudo={{
        pedir: () => gerar.mutate({ tipo: 'CONTRATO', contraparteId, ano: q.data?.ano ?? 2027 }),
        pendente: gerar.isPending,
        pedido: gerar.isSuccess,
        erro: gerar.error,
      }}
    />
  )
}

export interface AcaoEstudo {
  pedir: () => void
  pendente: boolean
  pedido: boolean
  erro: unknown
}

/** Só a aparência: recebe os dados prontos, para a tela e para a conferência visual. */
export function PainelCasoConcreto({
  d,
  carregando,
  erro,
  papel,
  fechar,
  estudo,
}: {
  d: CasoConcretoContraparte | undefined
  carregando?: boolean
  erro?: unknown
  papel: 'FORNECEDOR' | 'CLIENTE'
  fechar: () => void
  estudo?: AcaoEstudo
}) {
  const q = { isPending: Boolean(carregando), isError: Boolean(erro), error: erro }
  const fornecedor = papel === 'FORNECEDOR'
  // Empresa do Simples: crédito e débito desta relação só existem se ela optar pelo híbrido.
  const noHibrido = Boolean(d?.creditoNoHibrido)
  const credito = (m: { creditoCbsIbs: number; simples: { creditoHibrido: number } | null }) => (noHibrido ? (m.simples?.creditoHibrido ?? 0) : m.creditoCbsIbs)
  const debito = (m: { debitoCbsIbs: number; simples: { debitoHibrido: number } | null }) => (noHibrido ? (m.simples?.debitoHibrido ?? 0) : m.debitoCbsIbs)
  const temDas = Boolean(d?.totais?.simples && d.totais.simples.dasPuro > 0)
  // Para empresa do Simples o imposto de hoje É o DAS: mostrar "tributos hoje" (destaque da nota, quase sempre
  // de terceiros) ao lado dele fazia parecer que ela paga quase nada. E sem crédito nesta relação, "líquido"
  // seria uma cópia da coluna do débito.
  const colunasDas = temDas && !fornecedor
  const temLiquido = Boolean(d?.totais && credito(d.totais) > 0 && debito(d.totais) > 0)

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto bg-black/30 p-0 sm:items-start sm:p-6" onClick={fechar}>
      <div className="flex w-full max-w-5xl flex-col gap-4 rounded-t-lg bg-painel p-5 shadow-xl sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        {q.isPending && <p className="text-sm text-tinta-3">Carregando as notas…</p>}
        {q.isError && <Aviso>{mensagemDeErro(q.error)}</Aviso>}
        {d && (
          <>
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] text-tinta-3">{fornecedor ? 'Fornecedor' : 'Cliente'} · caso concreto</p>
                <h2 className="text-lg font-semibold">{d.contraparte.nome ?? d.contraparte.documento ?? '—'}</h2>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-tinta-2">
                  {d.contraparte.documento && <span className="num">{formatarCnpj(d.contraparte.documento)}</span>}
                  <Selo tom={d.contraparte.confirmacao === 'CONFIRMADO' ? 'ok' : 'neutro'}>
                    {ROTULO_REGIME_CONTRAPARTE[d.contraparte.regime as RegimeContraparte]}
                    {d.contraparte.confirmacao === 'CONFIRMADO' ? '' : ' (a confirmar)'}
                  </Selo>
                  {d.periodo.de && d.periodo.ate && (
                    <span>
                      notas de {mesCurto(d.periodo.de)} a {mesCurto(d.periodo.ate)} · {d.periodo.meses} {d.periodo.meses === 1 ? 'mês' : 'meses'}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {estudo && d.meses.length > 0 && (
                  <Botao onClick={estudo.pedir} disabled={estudo.pendente || estudo.pedido} title="Gera o PDF com esta conta, para levar à conversa">
                    {estudo.pedido ? 'estudo pedido' : 'Gerar estudo (PDF)'}
                  </Botao>
                )}
                <Botao variante="fantasma" onClick={fechar}>
                  Fechar
                </Botao>
              </div>
            </header>

            {estudo?.erro ? <Aviso>{mensagemDeErro(estudo.erro)}</Aviso> : null}
            {estudo?.pedido && <Aviso tom="info">O estudo deste contrato está sendo gerado — ele aparece em Relatórios em alguns segundos.</Aviso>}
            {!d.meses.length ? (
              <Aviso tom="info">Não há notas desta contraparte no período da simulação.</Aviso>
            ) : (
              <>
                <p className="text-[13px] text-tinta-2">
                  Valores reais das notas, mês a mês, <strong>sem projeção e sem média</strong>. Em {d.ano} a CBS e o IBS valem {percentualPpm((d.aliquotas?.cbs ?? 0) + (d.aliquotas?.ibs ?? 0))}
                  {d.aliquotas?.status === 'ESTIMATIVA' ? ' (alíquota ainda estimada)' : ''}.{' '}
                  {fornecedor
                    ? 'O crédito sai do regime desta contraparte: regime regular ou Simples híbrido dão crédito cheio; Simples puro, só a parcela que está no DAS dela; MEI e pessoa física, nada.'
                    : ''}
                </p>
                <p className="text-[11px] text-tinta-3 sm:hidden">Arraste a tabela para o lado para ver todas as colunas.</p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[44rem] text-[13px]">
                    <thead>
                      <tr className="border-b border-linha text-left text-[11px] text-tinta-3">
                        <th className="py-2 font-medium">Mês</th>
                        <th className="py-2 pl-4 text-right font-medium">Notas</th>
                        <th className="py-2 pl-4 text-right font-medium">{fornecedor ? 'Compras dele' : 'Vendas a ele'}</th>
                        {colunasDas ? (
                          <>
                            <th className="py-2 pl-4 text-right font-medium">DAS hoje</th>
                            <th className="py-2 pl-4 text-right font-medium">DAS no híbrido</th>
                            <th className="py-2 pl-4 text-right font-medium">IBS/CBS na nota</th>
                            <th className="py-2 pl-4 text-right font-medium">Total no híbrido</th>
                          </>
                        ) : (
                          <>
                            <th className="py-2 pl-4 text-right font-medium">Tributos hoje</th>
                            <th className="py-2 pl-4 text-right font-medium">{fornecedor ? 'Crédito de IBS/CBS' : 'Imposto por fora'}</th>
                            {temLiquido && <th className="py-2 pl-4 text-right font-medium">Crédito</th>}
                            {temLiquido && <th className="py-2 pl-4 text-right font-medium">Líquido</th>}
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {[...d.meses, ...(d.totais ? [{ ...d.totais, competencia: null }] : [])].map((m) => {
                        const total = m.competencia === null
                        const hoje = m.tributosHoje.icms + m.tributosHoje.ipi + m.tributosHoje.pis + m.tributosHoje.cofins + m.tributosHoje.iss
                        const cel = `num whitespace-nowrap pl-4 text-right ${total ? 'py-2' : 'py-1.5'}`
                        return (
                          <tr key={m.competencia ?? 'total'} className={total ? 'border-t-2 border-linha font-semibold' : 'border-b border-linha'}>
                            <td className={total ? 'py-2' : 'py-1.5'}>{total ? 'Total do período' : mesCurto(m.competencia!)}</td>
                            <td className={cel}>{fornecedor ? m.notasEntrada : m.notasSaida}</td>
                            <td className={cel}>{reais(fornecedor ? m.compras : m.vendas)}</td>
                            {colunasDas ? (
                              <>
                                <td className={cel}>{reais(m.simples?.dasPuro ?? 0)}</td>
                                <td className={cel}>{reais(m.simples?.dasHibrido ?? 0)}</td>
                                <td className={cel}>{reais(debito(m))}</td>
                                <td className={cel}>{reais((m.simples?.dasHibrido ?? 0) + debito(m))}</td>
                              </>
                            ) : (
                              <>
                                <td className={`${cel} ${total ? '' : 'text-tinta-2'}`}>{reais(hoje)}</td>
                                <td className={fornecedor ? `${cel} text-ok` : cel}>{reais(fornecedor ? credito(m) : debito(m))}</td>
                                {temLiquido && <td className={`${cel} text-ok`}>{reais(credito(m))}</td>}
                                {temLiquido && <td className={cel}>{reais(debito(m) - credito(m))}</td>}
                              </>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {colunasDas && d.totais?.simples && (
                  <section className="flex flex-col gap-2 rounded-md bg-fundo p-4">
                    <h3 className="text-[13px] font-semibold">A conta do período, fechando</h3>
                    <dl className="flex flex-col">
                      {(
                        [
                          ['Hoje a empresa recolhe, só o DAS', d.totais.simples.dasPuro, ''],
                          ['No híbrido: DAS deste contrato', d.totais.simples.dasHibrido, ''],
                          ['No híbrido: IBS/CBS destacado por fora na nota', debito(d.totais), ''],
                          ['Total recolhido no híbrido', d.totais.simples.dasHibrido + debito(d.totais), 'forte'],
                          ['Menos o que vem do cliente, cobrado na nota (ele credita tudo)', -debito(d.totais), 'suave'],
                          ['Sai do caixa da empresa', d.totais.simples.dasHibrido, 'forte'],
                          [`Contra os ${reais(d.totais.simples.dasPuro)} de hoje`, d.totais.simples.dasHibrido - d.totais.simples.dasPuro, 'saldo'],
                        ] as Array<[string, number, string]>
                      ).map(([rotulo, valor, tom]) => (
                        <div
                          key={rotulo}
                          className={`flex items-baseline justify-between gap-4 border-b border-linha py-1.5 text-[12px] last:border-0 ${tom === 'forte' || tom === 'saldo' ? 'font-semibold' : ''} ${tom === 'suave' ? 'text-tinta-3' : ''}`}
                        >
                          <dt>{rotulo}</dt>
                          <dd className={`num whitespace-nowrap ${tom === 'saldo' ? (valor > 0 ? 'text-alerta' : 'text-ok') : ''}`}>
                            {tom === 'saldo' && valor < 0 ? '' : tom === 'saldo' ? '+' : ''}
                            {reais(valor)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <p className="text-[12px] text-tinta-2">
                      Repare: no híbrido a empresa recolhe <strong>{reais(d.totais.simples.dasHibrido + debito(d.totais))}</strong>,{' '}
                      {reais(d.totais.simples.dasHibrido + debito(d.totais) - d.totais.simples.dasPuro)} a <strong>mais</strong> do que hoje. A diferença não é custo dela: o IBS/CBS é
                      cobrado do cliente por fora, na nota, e ele aproveita tudo como crédito. Descontando essa parte, o que sai do caixa é{' '}
                      {reais(d.totais.simples.dasHibrido)} — {reais(d.totais.simples.dasPuro - d.totais.simples.dasHibrido)} a menos do que hoje. Esta conta mantém o preço de hoje; o
                      preço recalculado está abaixo.
                    </p>
                  </section>
                )}
                {d.totais && (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-md bg-fundo p-3">
                      <p className="text-[11px] text-tinta-3">{fornecedor ? 'Compras no período' : 'Vendas no período'}</p>
                      <p className="num text-base font-semibold">{reais(fornecedor ? d.totais.compras : d.totais.vendas)}</p>
                      <p className="text-[11px] text-tinta-3">{fornecedor ? d.totais.notasEntrada : d.totais.notasSaida} nota(s), soma dos meses</p>
                    </div>
                    <div className="rounded-md bg-fundo p-3">
                      <p className="text-[11px] text-tinta-3">
                        {fornecedor ? 'Crédito de CBS/IBS no período' : 'Imposto por fora na nota, no período'}
                        {noHibrido ? ' (se optar pelo híbrido)' : ''}
                      </p>
                      <p className="num text-base font-semibold">{reais(fornecedor ? credito(d.totais) : debito(d.totais))}</p>
                      <p className="text-[11px] text-tinta-3">
                        {fornecedor ? `pelo regime dele: ${ROTULO_REGIME_CONTRAPARTE[d.contraparte.regime as RegimeContraparte].toLowerCase()}` : `cobrado do cliente em ${d.ano}, que credita tudo`}
                      </p>
                    </div>
                    {temDas ? (
                      <div className="rounded-md bg-navy p-3 text-white">
                        <p className="text-[11px] text-white/75">DAS deste contrato</p>
                        <p className="num text-base font-semibold">
                          {reais(d.totais.simples?.dasPuro ?? 0)} → {reais(d.totais.simples?.dasHibrido ?? 0)}
                        </p>
                        <p className="text-[11px] text-white/75">puro → híbrido, somando os meses do período</p>
                      </div>
                    ) : (
                      <div className="rounded-md bg-navy p-3 text-white">
                        <p className="text-[11px] text-white/75">Líquido no período</p>
                        <p className="num text-base font-semibold">{reais(debito(d.totais) - credito(d.totais))}</p>
                        <p className="text-[11px] text-white/75">débito menos crédito, só desta relação</p>
                      </div>
                    )}
                  </div>
                )}
                {d.repasse && !fornecedor && <RepasseContraparte r={d.repasse} ano={d.ano} meses={d.periodo.meses} />}
                {d.avisos.length > 0 && (
                  <ul className="list-disc space-y-1 pl-5 text-[11px] text-tinta-3">
                    {d.avisos.slice(0, 4).map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
